import * as Sentry from '@sentry/browser'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { PATH_HELPERS } from 'src/constants/navigation'
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import { Button, Icon, IconButton, useToast } from '@homebotapp/hb-react-component-catalog'
import { Listing, ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'

export const MSG = defineMessages({
  listingLinkCopied: {
    id: 'HomeSearch.shareListingButton.listingLinkCopied',
    defaultMessage: 'Listing link copied!'
  },
  shareButton: {
    id: 'HomeSearch.shareListingButton.shareButton',
    defaultMessage: 'Share'
  },
  copyButton: {
    id: 'HomeSearch.shareListingButton.copyButton',
    defaultMessage: 'Copy'
  },
  shareTitle: {
    id: 'HomeSearch.shareListingButton.shareTitle',
    defaultMessage: 'Homebot Home Search Listing'
  }
})

interface ShareListingButtonProps {
  useIconButton?: boolean
  listing: ListingCompact | Listing
}

export const ShareListingButton = ({ listing, useIconButton }: ShareListingButtonProps) => {
  const customerId = useSelector(selectCustomerId)
  const dispatch = useDispatch()
  const toast = useToast()
  const intl = useIntl()

  const handleShareListing = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const pathWithUtmCampaign = PATH_HELPERS.listing.buildPath(customerId, listing.id) + '?utm_campaign=share_link'

    navigator
      .share({
        title: intl.formatMessage(MSG.shareTitle),
        url: pathWithUtmCampaign
      })
      .catch(e => Sentry.captureException(e))
  }

  const handleCopyListing = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const pathWithUtmCampaign = PATH_HELPERS.listing.buildPath(customerId, listing.id) + '?utm_campaign=share_link'

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${window.location.origin}${pathWithUtmCampaign}`)

      dispatch(
        toast({
          description: intl.formatMessage(MSG.listingLinkCopied),
          status: 'success'
        })
      )
    }
  }

  if (navigator.share) {
    return useIconButton ? (
      <IconButton
        mt={1}
        size='sm'
        variant='outline'
        colorScheme='secondary'
        bg='blackAlpha.700'
        _hover={{
          bg: 'blackAlpha.900',
          _light: {
            bg: 'whiteAlpha.900'
          }
        }}
        _light={{ bg: 'whiteAlpha.700' }}
        icon={<Icon name='share-arrow' h={4} w={4} />}
        aria-label={intl.formatMessage(MSG.shareButton)}
        onClick={e => handleShareListing(e)}
        tracking={{
          guid: 'hGfGMQ94xCd6NGcMLH2xQY',
          ui_context: 'Listing Card',
          descriptor: 'Share a Listing'
        }}
      />
    ) : (
      <Button
        size='sm'
        colorScheme='primary'
        onClick={e => handleShareListing(e)}
        leftIcon={<Icon name='share-arrow' h={4} w={4} />}
        tracking={{
          guid: '58i4ZhsSzkDtJ6HkswKK3N',
          ui_context: 'Listing Card',
          descriptor: 'Share a Listing'
        }}
      >
        {intl.formatMessage(MSG.shareButton)}
      </Button>
    )
  }
  if (navigator.clipboard) {
    return useIconButton ? (
      <IconButton
        mt={1}
        size='sm'
        variant='outline'
        colorScheme='secondary'
        bg='blackAlpha.700'
        _hover={{
          bg: 'blackAlpha.900',
          _light: {
            bg: 'whiteAlpha.900'
          }
        }}
        _light={{ bg: 'whiteAlpha.700' }}
        icon={<Icon name='copy' h={4} w={4} />}
        onClick={async e => handleCopyListing(e)}
        aria-label={intl.formatMessage(MSG.copyButton)}
        tracking={{
          guid: 'wKkdFtrEoh6YRjsVFwEh6X',
          ui_context: 'Listing Card',
          descriptor: 'Copy a Listing'
        }}
      />
    ) : (
      <Button
        size='sm'
        colorScheme='primary'
        onClick={async e => handleCopyListing(e)}
        leftIcon={<Icon name='copy' h={4} w={4} />}
        tracking={{
          guid: 'r5YQ7ZfxAyCU1UnPbTS9Cf',
          ui_context: 'Listing Card',
          descriptor: 'Copy a Listing'
        }}
      >
        {intl.formatMessage(MSG.copyButton)}
      </Button>
    )
  }
  return null
}
